import { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { toast } from 'sonner'
import { ApiError } from '@/lib/api-error'
import { createPlan, updatePlan, MembershipPlan } from '@/api/plans'
import { CreditCard, Loader2 } from 'lucide-react'

interface Props {
  open: boolean
  plan?: MembershipPlan | null
  onClose: () => void
  onSaved: (plan: MembershipPlan) => void
}

const DURATION_PRESETS = [
  { label: '1 mes',   days: 30  },
  { label: '2 meses', days: 60  },
  { label: '3 meses', days: 90  },
  { label: '1 año',   days: 365 },
]

export function PlanDialog({ open, plan, onClose, onSaved }: Props) {
  const [name, setName]               = useState('')
  const [description, setDescription] = useState('')
  const [durationDays, setDurationDays] = useState(30)
  const [graceDays, setGraceDays]     = useState(0)
  const [price, setPrice]             = useState(0)
  const [isActive, setIsActive]       = useState(true)
  const [saving, setSaving]           = useState(false)
  const [errors, setErrors]           = useState<Record<string, string>>({})

  useEffect(() => {
    if (plan) {
      setName(plan.name)
      setDescription(plan.description ?? '')
      setDurationDays(plan.duration_days)
      setGraceDays(plan.grace_days)
      setPrice(Number(plan.price))
      setIsActive(plan.is_active)
    } else {
      setName('')
      setDescription('')
      setDurationDays(30)
      setGraceDays(0)
      setPrice(0)
      setIsActive(true)
    }
    setErrors({})
  }, [plan, open])

  function validate(): boolean {
    const errs: Record<string, string> = {}
    if (!name.trim())      errs.name         = 'Nombre es requerido'
    if (price <= 0)        errs.price        = 'El precio debe ser mayor a 0'
    if (durationDays < 1)  errs.durationDays = 'La duración debe ser de al menos 1 día'
    if (graceDays < 0)     errs.graceDays    = 'Los días de gracia no pueden ser negativos'
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!validate()) return
    setSaving(true)
    try {
      let saved: MembershipPlan
      if (plan) {
        const r = await updatePlan(plan.id, { name, description: description || undefined, durationDays, graceDays, price, isActive })
        saved = r.plan
      } else {
        const r = await createPlan({ name, description: description || undefined, durationDays, graceDays, price })
        saved = r.plan
      }
      toast.success(plan ? 'Plan actualizado' : 'Plan creado')
      onSaved(saved)
      onClose()
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Error al guardar')
    } finally {
      setSaving(false)
    }
  }

  const isEdit = Boolean(plan)

  const inputCls = 'w-full bg-background border border-border rounded-xl px-4 py-2.5 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent/50 transition-all'

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="bg-surface border-border text-text-primary sm:max-w-2xl">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-9 h-9 rounded-xl bg-accent/10 border border-accent/20 flex items-center justify-center shrink-0">
              <CreditCard className="w-4 h-4 text-accent" />
            </div>
            <DialogTitle className="text-base font-bold">
              {isEdit ? 'Editar plan' : 'Nuevo plan de membresía'}
            </DialogTitle>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="mt-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">

            {/* ── Columna izquierda: Nombre + Descripción ── */}
            <div className="flex flex-col gap-5">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-text-muted uppercase tracking-wide">
                  Nombre <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  maxLength={100}
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Ej: Plan mensual, Plan trimestral…"
                  className={inputCls}
                />
                {errors.name && <p className="text-red-500 text-xs">{errors.name}</p>}
              </div>

              <div className="flex flex-col gap-1.5 flex-1">
                <label className="text-xs font-semibold text-text-muted uppercase tracking-wide">
                  Descripción <span className="text-text-muted font-normal normal-case">(opcional)</span>
                </label>
                <textarea
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  maxLength={500}
                  rows={4}
                  placeholder="Beneficios o detalles del plan"
                  className={inputCls + ' resize-none flex-1'}
                />
              </div>
            </div>

            {/* ── Columna derecha: Precio + Duración + Gracia + Estado ── */}
            <div className="flex flex-col gap-5">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-text-muted uppercase tracking-wide">
                  Precio <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center bg-background border border-border rounded-xl px-4 py-2.5 gap-2 focus-within:ring-2 focus-within:ring-accent/30 focus-within:border-accent/50 transition-all">
                  <span className="text-text-muted text-sm font-semibold">$</span>
                  <input
                    type="number"
                    required
                    min={1}
                    step={100}
                    value={price}
                    onChange={e => setPrice(Number(e.target.value))}
                    className="flex-1 bg-transparent text-text-primary text-sm font-bold focus:outline-none"
                  />
                  <span className="text-text-muted text-xs font-medium">COP</span>
                </div>
                {price > 0 && (
                  <p className="text-accent text-xs font-semibold">${Number(price).toLocaleString('es-CO')} COP</p>
                )}
                {errors.price && <p className="text-red-500 text-xs">{errors.price}</p>}
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold text-text-muted uppercase tracking-wide">
                  Duración <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2 flex-wrap">
                  {DURATION_PRESETS.map(p => (
                    <button
                      key={p.days}
                      type="button"
                      onClick={() => setDurationDays(p.days)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors cursor-pointer ${
                        durationDays === p.days
                          ? 'bg-accent/10 text-accent border-accent/30'
                          : 'text-text-muted border-border hover:text-text-primary hover:bg-surface'
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    required
                    min={1}
                    step={1}
                    value={durationDays}
                    onChange={e => setDurationDays(Number(e.target.value))}
                    className={inputCls + ' w-24'}
                  />
                  <span className="text-text-muted text-sm">días</span>
                </div>
                {errors.durationDays && <p className="text-red-500 text-xs">{errors.durationDays}</p>}
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-text-muted uppercase tracking-wide">
                  Días de gracia
                </label>
                <input
                  type="number"
                  min={0}
                  step={1}
                  value={graceDays}
                  onChange={e => setGraceDays(Number(e.target.value))}
                  className={inputCls}
                />
                <p className="text-text-muted text-[10px] leading-snug">
                  Días extra antes de marcar como vencido
                </p>
                {errors.graceDays && <p className="text-red-500 text-xs">{errors.graceDays}</p>}
              </div>

              {isEdit && (
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-text-muted uppercase tracking-wide">
                    Estado
                  </label>
                  <button
                    type="button"
                    onClick={() => setIsActive(v => !v)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-semibold transition-colors cursor-pointer ${
                      isActive
                        ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-600 dark:text-emerald-400'
                        : 'bg-background border-border text-text-muted'
                    }`}
                  >
                    <span className={`w-2 h-2 rounded-full shrink-0 ${isActive ? 'bg-emerald-500' : 'bg-border'}`} />
                    {isActive ? 'Activo' : 'Inactivo'}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-5 mt-5 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 text-sm font-medium text-text-muted hover:text-text-primary transition-colors cursor-pointer"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving || !name.trim()}
              className="flex items-center gap-2 px-5 py-2.5 bg-accent text-background text-sm font-bold rounded-xl hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
            >
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {saving ? 'Guardando...' : isEdit ? 'Guardar cambios' : 'Crear plan'}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
